import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { clockAPI, talentAPI } from '../../utils/api';
import { formatTimeRange } from '../../utils/timeFormat';
import { acquireLocation } from '../../utils/geolocation';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import SelfieCapture from '../../components/SelfieCapture';
import PostEventRecapModal from '../../components/PostEventRecapModal';
import GPSConsentModal from '../../components/GPSConsentModal';
import GPSActiveBanner from '../../components/GPSActiveBanner';
import { MessageOpsButton } from '../../components/talent/MessageOpsButton';
import { lazy as Rlazy, Suspense as Rsuspense } from 'react';
// Iter-235l — Lazy-load to avoid visual-edits plugin cross-file crash.
const ShiftMetricsWizard = Rlazy(() => import('../../components/ShiftMetricsWizard'));
import {
  ArrowLeft, Clock, MapPin, LogIn, LogOut, Loader2, CheckCircle,
  AlertCircle, Calendar, Building2, Timer, XCircle, RefreshCw, Camera, Navigation
} from 'lucide-react';
import { toast } from 'sonner';

const ClockInOut = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [todaysBookings, setTodaysBookings] = useState([]);
  const [clockStatuses, setClockStatuses] = useState({});
  const [processing, setProcessing] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [selfieModal, setSelfieModal] = useState(null); // { booking, type: 'in'|'out' }
  const [pendingSelfie, setPendingSelfie] = useState({}); // { [bookingId]: { url, kind } }
  const [gpsDismissed, setGpsDismissed] = useState(false); // user acknowledged "continue without GPS"
  // Iter-220j (Sprint 1A) — auto-trigger the post-event recap modal
  // immediately after a successful clock-out so the talent leaves
  // feedback while it's fresh. Decoupled from payroll approval.
  const [postRecapBooking, setPostRecapBooking] = useState(null);
  // Iter-235l — After Post-Event Recap closes, open the Shift Metrics
  // wizard so the talent captures campaign performance data while it's
  // fresh in their mind. Holds the booking_id; cleared on close.
  const [metricsWizardBookingId, setMetricsWizardBookingId] = useState(null);
  // Iter-263 — GPS shift-level consent gate. Talent must accept per-shift
  // consent BEFORE clock-in is allowed. Also tracks which booking has an
  // active GPS tracking session so the banner stays visible.
  const [consentModal, setConsentModal] = useState(null); // { booking } when open
  const [shiftConsents, setShiftConsents] = useState({}); // { bookingId: true } after accept
  const [hardBlock, setHardBlock] = useState(null); // { booking, reason } when GPS denied

  useEffect(() => {
    fetchTodaysBookings();
    requestLocation();
  }, []);

  const requestLocation = async () => {
    // Iter-347 — Android + iOS location permission handling.
    // Some browsers (especially Android Chrome PWAs) require a user
    // gesture before firing the permission prompt reliably. The retry
    // button below covers that; this initial call still fires eagerly
    // in case the browser is willing.
    if (!('geolocation' in navigator)) {
      setLocationError('Your browser does not support GPS. Please switch to Chrome or Safari.');
      return;
    }

    // Query the Permissions API first (when available) so we can show a
    // targeted error message + instructions for re-enabling a previously-
    // denied permission on Android.
    let permissionState = null;
    try {
      if (navigator.permissions?.query) {
        const status = await navigator.permissions.query({ name: 'geolocation' });
        permissionState = status.state; // 'granted' | 'prompt' | 'denied'
      }
    } catch (_) { /* ignore, older browsers */ }

    try {
      // P0 (2026-08-18, Anita Currie / Dallas multi-talent report) — Two-stage
      // acquisition: high-accuracy GPS with 25s timeout, then low-accuracy
      // (network/WiFi/cell) fallback with 8s timeout. The old single-stage
      // 10s high-accuracy call reliably failed indoors on cellular data,
      // stranding talents at the "Please enable Location Services" modal
      // even when their OS permission was already granted.
      const loc = await acquireLocation();
      setCurrentLocation({ lat: loc.latitude, lng: loc.longitude });
      setLocationError(null);
      setGpsDismissed(false);
    } catch (e) {
      const denied = e?.code === 'permission_denied' || permissionState === 'denied';
      const androidHint = navigator.userAgent.includes('Android')
        ? ' On Android: open Settings → Apps → your browser → Permissions → Location, and enable it. Then return here and tap Retry.'
        : '';
      const iosHint = /iP(hone|ad)/.test(navigator.userAgent)
        ? ' On iPhone: Settings → your browser → Location → Allow. Then return here and tap Retry.'
        : '';
      setLocationError(
        denied
          ? `Location access is blocked.${androidHint}${iosHint} You can also continue without GPS (admin review required).`
          : (e?.message || 'Location unavailable. Move to a spot with a clearer sky view or wait a moment, then tap Retry. You can also continue without GPS (admin review required).'),
      );
    }
  };

  const fetchTodaysBookings = async () => {
    try {
      const response = await talentAPI.getBookings();
      const today = new Date().toISOString().split('T')[0];
      
      // Filter for today's confirmed/in-progress/completed bookings
      const todayBookings = response.data.filter(b => {
        const bookingDate = b.shift_date?.split('T')[0];
        return bookingDate === today && ['confirmed', 'in_progress', 'completed', 'checked_in'].includes(b.status);
      });
      
      setTodaysBookings(todayBookings);
      
      // Fetch clock status for each booking
      const statuses = {};
      for (const booking of todayBookings) {
        try {
          const statusRes = await clockAPI.getStatus(booking.id);
          statuses[booking.id] = statusRes.data;
        } catch (e) {
          // Ignore individual failures
        }
      }
      setClockStatuses(statuses);

      // Iter-263 — Hydrate per-shift consent state so we don't re-prompt the
      // talent every time they revisit this page after already accepting.
      const consentMap = {};
      await Promise.all(
        todayBookings.map(async (b) => {
          try {
            const r = await api.get(`/gps/attendance-status/${b.id}`);
            if (r.data?.has_shift_consent) consentMap[b.id] = true;
          } catch (_) { /* ignore */ }
        })
      );
      setShiftConsents((prev) => ({ ...prev, ...consentMap }));
    } catch (error) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  // Iter-263 — Hard block clock-in unless:
  //   (a) talent has accepted shift-level GPS consent
  //   (b) browser geolocation permission is granted (we already have a fix)
  // Returns true if all guards pass.
  const enforceClockInGuards = (booking) => {
    if (!shiftConsents[booking.id]) {
      setConsentModal({ booking });
      return false;
    }
    if (!currentLocation) {
      setHardBlock({
        booking,
        reason:
          'Location access is required for attendance verification on this assignment. ' +
          'Please enable Location Services in your device settings, then retry.',
      });
      return false;
    }
    return true;
  };

  // Iter-411t P0 — Onsite fallback for the ClockInOut page.
  //
  // Prior state: `enforceClockInGuards` opened a `hardBlock` modal with
  // only "Close" and "Retry Location" buttons. If the browser had denied
  // location permission (Samantha Lacroix's exact production incident),
  // the modal became a dead-end: talent physically onsite could not
  // clock in.
  //
  // Fix: submit the clock-in with `gps_fallback=true, selfie_fallback=true`
  // routed through `/clock/in`. The record is flagged for admin review
  // and Reliability Score is protected. Selfie is optional — the fallback
  // is specifically for cases where the device sensors are unavailable.
  const handleOnsiteFallback = async (type = 'in') => {
    const booking = hardBlock?.booking;
    if (!booking) return;
    setHardBlock(null);
    setProcessing(booking.id);
    // Stable client_request_id so a retry never creates a duplicate.
    const idempotencyStorageKey = `clock-req-${type}-${booking.id}`;
    let clientRequestId = sessionStorage.getItem(idempotencyStorageKey);
    if (!clientRequestId) {
      clientRequestId = (window.crypto?.randomUUID?.() ||
        `cio-onsite-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
      sessionStorage.setItem(idempotencyStorageKey, clientRequestId);
    }
    try {
      // V414 — reuse the persisted attempted-at across the fallback
      // path so the audit trail sees ONE arrival time even after retries.
      const attemptedAtStorageKey = `clock-attempted-at-${type}-${booking.id}`;
      let clientAttemptedAt = sessionStorage.getItem(attemptedAtStorageKey);
      if (!clientAttemptedAt) {
        clientAttemptedAt = new Date().toISOString();
        sessionStorage.setItem(attemptedAtStorageKey, clientAttemptedAt);
      }
      const payload = {
        booking_id: booking.id,
        latitude: null,
        longitude: null,
        selfie_url: undefined,
        selfie_fallback: true,
        gps_fallback: true,
        fallback_reason: 'onsite_fallback_gps_unavailable',
        client_request_id: clientRequestId,
        client_attempted_at: clientAttemptedAt,
      };
      const cfg = { headers: { 'X-Client-Request-ID': clientRequestId }, timeout: 45000 };
      const res = type === 'in'
        ? await clockAPI.clockIn(payload, cfg)
        : await clockAPI.clockOut(payload, cfg);
      toast.success(res.data?.message || (type === 'in' ? 'Clock-in submitted for admin review' : 'Clock-out submitted for admin review'), { duration: 8000 });
      toast.warning('Your attendance and Reliability Score are protected while admin reviews.', { duration: 7000 });
      sessionStorage.removeItem(idempotencyStorageKey);
      sessionStorage.removeItem(attemptedAtStorageKey);
      fetchTodaysBookings();
    } catch (error) {
      const msg = error?.response?.data?.detail || `Failed to ${type === 'in' ? 'clock in' : 'clock out'} without GPS`;
      toast.error(typeof msg === 'string' ? msg : (msg?.message || 'Fallback failed — please try again.'), { duration: 9000 });
    } finally {
      setProcessing(null);
    }
  };

  const handleClockIn = async (booking) => {
    // GPS Phase A — re-run guards in case state changed between button click
    // and modal confirmation. Bail out silently if anything is still missing.
    if (!enforceClockInGuards(booking)) return;
    const selfie = pendingSelfie[booking.id];
    if (!selfie || selfie.kind !== 'check_in') {
      // Require selfie before clock-in
      setConfirmModal(null);
      setSelfieModal({ booking, type: 'in' });
      return;
    }
    setProcessing(booking.id);
    try {
      // V414 — preserve original arrival time across the confirm-modal path.
      const attemptedAtStorageKey = `clock-attempted-at-in-${booking.id}`;
      let clientAttemptedAt = sessionStorage.getItem(attemptedAtStorageKey);
      if (!clientAttemptedAt) {
        clientAttemptedAt = new Date().toISOString();
        sessionStorage.setItem(attemptedAtStorageKey, clientAttemptedAt);
      }
      const res = await clockAPI.clockIn({
        booking_id: booking.id,
        latitude: currentLocation?.lat,
        longitude: currentLocation?.lng,
        selfie_url: selfie.url,
        client_attempted_at: clientAttemptedAt,
      });
      
      toast.success(res.data.message || 'Clocked in successfully!');
      setConfirmModal(null);
      setPendingSelfie((p) => { const n = { ...p }; delete n[booking.id]; return n; });
      sessionStorage.removeItem(attemptedAtStorageKey);
      fetchTodaysBookings();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to clock in';
      toast.error(msg);
    } finally {
      setProcessing(null);
    }
  };

  const handleClockOut = async (booking) => {
    const selfie = pendingSelfie[booking.id];
    if (!selfie || selfie.kind !== 'check_out') {
      setConfirmModal(null);
      setSelfieModal({ booking, type: 'out' });
      return;
    }
    setProcessing(booking.id);
    try {
      const res = await clockAPI.clockOut({
        booking_id: booking.id,
        latitude: currentLocation?.lat,
        longitude: currentLocation?.lng,
        selfie_url: selfie.url,
      });
      
      toast.success(`Clocked out! Hours worked: ${res.data.hours_worked}`);
      setConfirmModal(null);
      setPendingSelfie((p) => { const n = { ...p }; delete n[booking.id]; return n; });
      // Iter-250 — Community engagement nudge: when someone finishes a shift,
      // surface a non-blocking Community Feed CTA so they're prompted to share.
      // The PostEventRecapModal still opens for the rating workflow; this toast
      // is purely a soft visibility lift for the Community Feed.
      setTimeout(() => {
        toast(
          (t) => (
            <div className="flex flex-col gap-2 max-w-xs">
              <div className="text-sm font-semibold text-slate-900">🎉 Shift complete!</div>
              <div className="text-xs text-slate-600 leading-snug">
                Share a photo, congratulate your teammates, or post a quick recap to the
                community.
              </div>
              <div className="flex gap-1.5 mt-1">
                <button
                  onClick={() => { toast.dismiss(t.id); navigate('/community?compose=1'); }}
                  className="px-2.5 py-1 rounded bg-fuchsia-600 text-white text-[11px] font-semibold hover:bg-fuchsia-700"
                  data-testid="clockout-community-post-btn"
                >
                  Post to Community
                </button>
                <button
                  onClick={() => toast.dismiss(t.id)}
                  className="px-2.5 py-1 rounded border border-slate-200 text-slate-600 text-[11px] hover:bg-slate-50"
                  data-testid="clockout-community-skip-btn"
                >
                  Maybe later
                </button>
              </div>
            </div>
          ),
          { duration: 12000, position: 'top-center' }
        );
      }, 1200);
      // Iter-220j — immediately open the post-event recap modal for this
      // booking so the talent leaves feedback before navigating away.
      setPostRecapBooking({
        booking_id: booking.id,
        id: booking.id,
        shift_title: booking.shift?.venue_name || booking.shift?.description,
        client_name: booking.shift?.client_company_name || booking.shift?.client_name,
        shift_end: booking.shift?.end_time
          ? `${booking.shift?.shift_date}T${booking.shift?.end_time}`
          : null,
      });
      fetchTodaysBookings();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to clock out';
      toast.error(msg);
    } finally {
      setProcessing(null);
    }
  };

  const onSelfieCaptured = ({ url, storage_path, fallback = false, reason = null }) => {
    if (!selfieModal) return;
    const { booking, type } = selfieModal;
    const kind = type === 'in' ? 'check_in' : 'check_out';
    // Iter-149 fallback: clock in/out without selfie, flagged for review
    const selfie_fallback = fallback || !url;
    const gps_fallback = !currentLocation;
    const fallback_reason =
      reason ||
      (selfie_fallback && gps_fallback
        ? 'No selfie and no GPS'
        : selfie_fallback
          ? 'No selfie'
          : gps_fallback
            ? 'No GPS'
            : null);

    if (!selfie_fallback) {
      // Persist BOTH the served URL and the storage_path — storage_path
      // lets us reuse the exact same object on a retry without a second
      // upload (Iter-342 selfie-reuse guarantee).
      setPendingSelfie((prev) => ({ ...prev, [booking.id]: { url, storage_path, kind } }));
    }

    // Iter-342 — stable client_request_id per clock-in/out attempt.
    // Persist to sessionStorage so a full page reload (network switch,
    // browser resurrection) still reuses it and can never create a
    // duplicate attendance record within the 30-min server dedup window.
    const idempotencyStorageKey = `clock-req-${type}-${booking.id}`;
    let clientRequestId = sessionStorage.getItem(idempotencyStorageKey);
    if (!clientRequestId) {
      clientRequestId = (window.crypto?.randomUUID?.() ||
        `cio-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
      sessionStorage.setItem(idempotencyStorageKey, clientRequestId);
    }
    // V414 — preserve the ORIGINAL arrival time across retries. Even if
    // the app takes 40 minutes to persist a successful clock-in due to
    // network / upload / gate failures, admin sees the true onsite time
    // in the audit ledger and payroll / reliability read
    // COALESCE(clock_in_actual_arrival, clock_in_time).
    const attemptedAtStorageKey = `clock-attempted-at-${type}-${booking.id}`;
    let clientAttemptedAt = sessionStorage.getItem(attemptedAtStorageKey);
    if (!clientAttemptedAt) {
      clientAttemptedAt = new Date().toISOString();
      sessionStorage.setItem(attemptedAtStorageKey, clientAttemptedAt);
    }

    (async () => {
      setProcessing(booking.id);
      const payload = {
        booking_id: booking.id,
        latitude: currentLocation?.lat,
        longitude: currentLocation?.lng,
        selfie_url: url || undefined,
        selfie_fallback,
        gps_fallback,
        fallback_reason,
        client_request_id: clientRequestId,
        client_attempted_at: clientAttemptedAt,
      };
      const cfg = { headers: { 'X-Client-Request-ID': clientRequestId }, timeout: 45000 };

      // Iter-342 — one automatic retry on network / CF 520 style
      // failures. Manual retries after that are handled by the user
      // seeing the toast and re-tapping Clock In / Clock Out (the same
      // client_request_id from sessionStorage prevents duplicates).
      const attempt = async () =>
        type === 'in'
          ? await clockAPI.clockIn(payload, cfg)
          : await clockAPI.clockOut(payload, cfg);

      try {
        let res;
        try {
          res = await attempt();
        } catch (first) {
          const status = first?.response?.status;
          const isRetryable = !first?.response || (status >= 500 && status < 600);
          if (!isRetryable) throw first;
          await new Promise((r) => setTimeout(r, 800));
          res = await attempt();
        }

        if (type === 'in') {
          toast.success(res.data.message || 'Clocked in successfully!');
        } else {
          toast.success(`Clocked out! Hours worked: ${res.data.hours_worked}`);
        }
        if (res.data?.needs_review) {
          toast.warning('Flagged for admin review — please contact your manager if pay is affected.', { duration: 7000 });
        }
        // Iter-356 (P0-355) — trust the resolver-attached attendance
        // state to update the card immediately. Prevents the "Clock-Out
        // Missing" tile from lingering after a successful clock-out.
        const attendance = res.data?.attendance_state;
        if (attendance?.found) {
          setClockStatuses((prev) => ({
            ...prev,
            [booking.id]: {
              ...(prev[booking.id] || {}),
              is_clocked_in: attendance.is_clocked_in,
              is_clocked_out: attendance.is_clocked_out,
              clock_in_time: attendance.clock_in_time,
              clock_out_time: attendance.clock_out_time,
              hours_worked: attendance.paid ? undefined : (prev[booking.id]?.hours_worked),
              attendance_state: attendance,
            },
          }));
        }
        // Success → clear both the pending selfie AND the idempotency
        // key so the NEXT clock-in on a different booking starts fresh.
        setPendingSelfie((p) => { const n = { ...p }; delete n[booking.id]; return n; });
        sessionStorage.removeItem(idempotencyStorageKey);
        // V414 — clear the preserved attempted-at so a fresh clock-out
        // (or a re-clock-in tomorrow) captures its own real start time.
        sessionStorage.removeItem(attemptedAtStorageKey);
        // Iter-356 — after clock-out we still refetch to pull anything
        // that changed downstream (rating rows, recap flags), but the UI
        // already flipped to "Shift Complete" via the resolver payload.
        fetchTodaysBookings();
        // Iter-220j — if we just clocked out, open the recap modal.
        if (type === 'out') {
          setPostRecapBooking({
            booking_id: booking.id,
            id: booking.id,
            shift_title: booking.venue_name || booking.shift?.venue_name || booking.shift?.description,
            client_name: booking.shift?.client_company_name || booking.shift?.client_name,
            shift_end: booking.shift?.end_time
              ? `${booking.shift?.shift_date}T${booking.shift?.end_time}`
              : null,
          });
        }
      } catch (error) {
        const status = error?.response?.status;
        const retryable = !error?.response || (status >= 500 && status < 600);
        const msg = error?.response?.data?.detail || `Failed to clock ${type === 'in' ? 'in' : 'out'}`;
        // The selfie stays in pendingSelfie — user's next tap will
        // reuse it AND the same client_request_id, so this is safe to
        // retry without any duplicate risk.
        toast.error(
          retryable
            ? `${msg} — your selfie is saved. Tap Clock ${type === 'in' ? 'In' : 'Out'} again to retry.`
            : msg,
          { duration: 8000 },
        );
      } finally {
        setProcessing(null);
      }
    })();
  };

  const openDirections = (booking) => {
    // Prefer event coordinates when present, otherwise a free-text fallback
    const addr = [booking.venue_address, booking.city, booking.state].filter(Boolean).join(', ');
    let url;
    if (booking.venue_lat && booking.venue_lng) {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      url = isIOS
        ? `https://maps.apple.com/?daddr=${booking.venue_lat},${booking.venue_lng}`
        : `https://www.google.com/maps/dir/?api=1&destination=${booking.venue_lat},${booking.venue_lng}`;
    } else if (addr) {
      url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}`;
    } else {
      toast.error('No venue location available');
      return;
    }
    window.open(url, '_blank', 'noopener');
  };

  const getStatusBadge = (status, clockStatus) => {
    if (clockStatus?.is_clocked_out) {
      return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Completed</Badge>;
    }
    if (clockStatus?.is_clocked_in) {
      return <Badge className="bg-green-100 text-green-800 border-green-200 animate-pulse">Clocked In</Badge>;
    }
    if (status === 'confirmed') {
      return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Ready to Clock In</Badge>;
    }
    return <Badge variant="outline">{status}</Badge>;
  };

  const canClockIn = (booking, clockStatus) => {
    return booking.status === 'confirmed' && !clockStatus?.is_clocked_in;
  };

  const canClockOut = (booking, clockStatus) => {
    return clockStatus?.is_clocked_in && !clockStatus?.is_clocked_out;
  };

  const isLateCheckIn = (booking) => {
    if (!booking.start_time) return false;
    const now = new Date();
    const [hours, minutes] = booking.start_time.split(':').map(Number);
    const shiftStart = new Date();
    shiftStart.setHours(hours, minutes, 0, 0);
    return now > shiftStart;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#03DFFC]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="clock-in-out-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-gray-900">Clock In / Out</h1>
            <p className="text-xs text-gray-500">Today&apos;s Gigs</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchTodaysBookings}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Location Status */}
        {locationError && !gpsDismissed && (
          <Alert variant="warning" className="bg-amber-50 border-amber-300" data-testid="gps-error-alert">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-900">
              <div className="font-semibold mb-1">Clock-In Issue Detected</div>
              <div className="text-sm mb-3">{locationError}</div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={requestLocation} data-testid="gps-retry-btn">
                  <RefreshCw className="w-3 h-3 mr-1" /> Retry location
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-amber-400 text-amber-800 hover:bg-amber-100"
                  onClick={() => setGpsDismissed(true)}
                  data-testid="gps-dismiss-btn"
                >
                  Continue without GPS
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {currentLocation && (
          <Alert className="bg-green-50 border-green-200">
            <MapPin className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              GPS location active - your check-in will be verified
            </AlertDescription>
          </Alert>
        )}

        {/* Today's Date */}
        <div className="flex items-center gap-2 text-gray-600">
          <Calendar className="w-4 h-4" />
          <span className="text-sm font-medium">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </span>
        </div>

        {/* No Bookings */}
        {todaysBookings.length === 0 && (
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="py-12 text-center">
              <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">No Gigs Today</h3>
              <p className="text-sm text-gray-500 mb-4">You don&apos;t have any confirmed gigs scheduled for today.</p>
              <Button variant="outline" onClick={() => navigate('/talent/bookings')}>
                View All Bookings
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Bookings List */}
        {todaysBookings.map(booking => {
          const clockStatus = clockStatuses[booking.id];
          const isLate = isLateCheckIn(booking);
          
          return (
            <Card 
              key={booking.id} 
              className={`border-2 transition-all ${
                clockStatus?.is_clocked_in && !clockStatus?.is_clocked_out 
                  ? 'border-green-300 bg-green-50/30' 
                  : clockStatus?.is_clocked_out 
                    ? 'border-blue-200 bg-blue-50/30'
                    : 'border-gray-200'
              }`}
              data-testid={`booking-card-${booking.id}`}
            >
              <CardContent className="p-4">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">{booking.venue_name}</h3>
                    <p className="text-sm text-gray-500 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {booking.city}, {booking.state}
                    </p>
                  </div>
                  {getStatusBadge(booking.status, clockStatus)}
                </div>

                {/* Time Info */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-gray-100 rounded-lg p-3">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Shift Time</p>
                    <p className="font-semibold text-gray-900">
                      {formatTimeRange(booking.start_time, booking.end_time)}
                    </p>
                  </div>
                  <div className="bg-gray-100 rounded-lg p-3">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Role</p>
                    <p className="font-semibold text-gray-900">{booking.role_name}</p>
                  </div>
                </div>

      {/* Iter-263 — Persistent banner above the booking card during active shifts.
          Auto-starts GPS tracking, records breadcrumbs every 90s, and flags the
          shift for admin review if location services fail mid-shift. */}
                {clockStatus?.is_clocked_in && !clockStatus?.is_clocked_out && (
                  <div className="mb-3">
                    <GPSActiveBanner
                      bookingId={booking.id}
                      active
                      onError={() => fetchTodaysBookings()}
                    />
                  </div>
                )}

                {/* Clock Status Display */}
                {clockStatus && (
                  <div className="bg-white border border-gray-200 rounded-lg p-3 mb-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Clock In</p>
                        {clockStatus.is_clocked_in ? (
                          <p className="font-medium text-green-700 flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" />
                            {clockStatus.clock_in_time}
                          </p>
                        ) : (
                          <p className="text-gray-400">--:--</p>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Clock Out</p>
                        {clockStatus.is_clocked_out ? (
                          <p className="font-medium text-blue-700 flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" />
                            {clockStatus.clock_out_time}
                          </p>
                        ) : (
                          <p className="text-gray-400">--:--</p>
                        )}
                      </div>
                    </div>
                    {clockStatus.hours_worked && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-sm text-gray-600">
                          <Timer className="w-4 h-4 inline mr-1" />
                          Hours worked: <span className="font-semibold">{clockStatus.hours_worked.toFixed(2)}</span>
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Late Warning */}
                {isLate && canClockIn(booking, clockStatus) && (
                  <Alert className="mb-4 bg-red-50 border-red-200">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-800">
                      Shift has started. Late check-in will be recorded.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openDirections(booking)}
                    className="flex items-center"
                    data-testid={`get-directions-btn-${booking.id}`}
                  >
                    <Navigation className="w-4 h-4 mr-2" />
                    Get Directions
                  </Button>

                  {canClockIn(booking, clockStatus) && (
                    <Button
                      onClick={() => {
                        if (!enforceClockInGuards(booking)) return;
                        setSelfieModal({ booking, type: 'in' });
                      }}
                      disabled={processing === booking.id}
                      className="flex-1 min-w-[140px] bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`clock-in-btn-${booking.id}`}
                    >
                      {processing === booking.id ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Camera className="w-4 h-4 mr-2" />
                      )}
                      Selfie + Clock In
                    </Button>
                  )}

                  {canClockOut(booking, clockStatus) && (
                    <Button
                      onClick={() => setSelfieModal({ booking, type: 'out' })}
                      disabled={processing === booking.id}
                      className="flex-1 min-w-[140px] bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid={`clock-out-btn-${booking.id}`}
                    >
                      {processing === booking.id ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Camera className="w-4 h-4 mr-2" />
                      )}
                      Selfie + Clock Out
                    </Button>
                  )}

                  {clockStatus?.is_clocked_out && (
                    <div className="flex-1 min-w-[140px] flex items-center justify-center text-blue-700 font-medium">
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Shift Complete
                    </div>
                  )}
                </div>

                {/* Phase-1 Live Event Messaging — persistent onsite ops CTA.
                    Always visible on the ClockInOut card so a talent can
                    reach iEvntür Ops even when clock-in fails or GPS is
                    unavailable. Draft is IndexedDB-persisted. */}
                <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2">
                  <div className="text-[11px] text-slate-500">
                    Need help? Message iEvntür directly — SMS + in-app alert.
                  </div>
                  <MessageOpsButton booking={booking} />
                </div>
              </CardContent>
            </Card>
          );
        })}

        {/* View All Bookings Link */}
        {todaysBookings.length > 0 && (
          <div className="text-center pt-4">
            <Button variant="outline" onClick={() => navigate('/talent/bookings')}>
              View All Bookings
            </Button>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <Dialog open={!!confirmModal} onOpenChange={() => setConfirmModal(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="clock-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {confirmModal?.type === 'in' ? (
                <>
                  <LogIn className="w-5 h-5 text-green-600" />
                  Confirm Clock In
                </>
              ) : (
                <>
                  <LogOut className="w-5 h-5 text-blue-600" />
                  Confirm Clock Out
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {confirmModal?.booking?.venue_name} - {confirmModal?.booking?.role_name}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Venue</span>
                <span className="font-medium">{confirmModal?.booking?.venue_name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Shift Time</span>
                <span className="font-medium">
                  {formatTimeRange(confirmModal?.booking?.start_time, confirmModal?.booking?.end_time)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Current Time</span>
                <span className="font-medium">{new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</span>
              </div>
              {currentLocation && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">GPS Status</span>
                  <span className="font-medium text-green-600">Active</span>
                </div>
              )}
            </div>

            {confirmModal?.type === 'out' && (
              <Alert className="mt-4 bg-amber-50 border-amber-200">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800 text-sm">
                  Make sure you&apos;ve completed all tasks before clocking out.
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmModal(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (confirmModal?.type === 'in') {
                  handleClockIn(confirmModal.booking);
                } else {
                  handleClockOut(confirmModal.booking);
                }
              }}
              disabled={processing === confirmModal?.booking?.id}
              className={confirmModal?.type === 'in' ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}
            >
              {processing === confirmModal?.booking?.id ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : confirmModal?.type === 'in' ? (
                <LogIn className="w-4 h-4 mr-2" />
              ) : (
                <LogOut className="w-4 h-4 mr-2" />
              )}
              {confirmModal?.type === 'in' ? 'Clock In Now' : 'Clock Out Now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Selfie Capture Modal */}
      <SelfieCapture
        open={!!selfieModal}
        onClose={() => setSelfieModal(null)}
        onCaptured={onSelfieCaptured}
        bookingId={selfieModal?.booking?.id}
        kind={selfieModal?.type === 'in' ? 'check_in' : 'check_out'}
      />

      {/* Iter-263 — Per-shift GPS consent gate. Hard-blocks clock-in until accepted. */}
      <GPSConsentModal
        open={!!consentModal}
        consentType="shift_check_in"
        bookingId={consentModal?.booking?.id}
        blocking={false}
        onClose={() => setConsentModal(null)}
        onAccept={() => {
          if (consentModal?.booking?.id) {
            setShiftConsents((prev) => ({ ...prev, [consentModal.booking.id]: true }));
          }
          const b = consentModal?.booking;
          setConsentModal(null);
          // Continue the clock-in flow now that consent is on file
          if (b && currentLocation) {
            setSelfieModal({ booking: b, type: 'in' });
          }
        }}
      />

      {/* Iter-263 / Iter-411t — Location Services alert with an ONSITE
          FALLBACK. The previous version was a dead-end (Close + Retry).
          Now the talent can also submit an admin-review record without
          GPS, protecting their attendance and Reliability Score. */}
      <Dialog open={!!hardBlock} onOpenChange={(o) => !o && setHardBlock(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="gps-hard-block-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertCircle className="w-5 h-5" /> GPS unavailable — recovery available
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              {hardBlock?.reason} If you&apos;re physically onsite you can
              also request an admin-verified check-in below.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs text-slate-500 space-y-1">
            <p><strong>iPhone:</strong> Settings → Privacy & Security → Location Services → Safari/Chrome → While Using.</p>
            <p><strong>Android:</strong> Settings → Apps → Browser → Permissions → Location → Allow.</p>
            <p><strong>Desktop:</strong> Click the lock/info icon in the address bar → Site Settings → Location → Allow.</p>
          </div>
          {/* Onsite Fallback — the P0 recovery path. Prominent solid cyan
              so it never reads as a secondary or disabled action. */}
          <Button
            onClick={() => handleOnsiteFallback(
              hardBlock?.booking && clockStatuses[hardBlock.booking.id]?.is_clocked_in ? 'out' : 'in'
            )}
            disabled={processing === hardBlock?.booking?.id}
            className="w-full h-14 text-base bg-cyan-600 hover:bg-cyan-700 text-white shadow-lg mt-2"
            data-testid="gps-hard-block-onsite-fallback-btn"
          >
            {processing === hardBlock?.booking?.id ? (
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            ) : (
              <AlertCircle className="w-5 h-5 mr-2" />
            )}
            I&apos;m onsite — request admin verification
          </Button>
          <p className="text-xs text-cyan-800 text-center px-2">
            No GPS or selfie required. Your attendance and Reliability
            Score are protected while admin reviews.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setHardBlock(null)}
              data-testid="gps-hard-block-close-btn"
            >
              Close
            </Button>
            <Button
              onClick={async () => {
                await requestLocation();
                setHardBlock(null);
              }}
              variant="outline"
              className="border-cyan-400 text-cyan-800 hover:bg-cyan-50"
              data-testid="gps-hard-block-retry-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Retry Location
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter-220j (Sprint 1A) — Post-event recap auto-trigger.
          Opens immediately after a successful clock-out. Talent can
          dismiss with "Remind me later" — the shift stays in
          `Post-Event Recap Required` state and the home reminder badge
          will keep nagging until they finish. */}
      <PostEventRecapModal
        isOpen={!!postRecapBooking}
        onClose={() => {
          // Iter-235l — Even if talent dismisses recap with "Remind me later",
          // we still offer the metrics wizard. Campaign data is independent
          // of the rating/recap flow.
          const bid = postRecapBooking?.booking_id || postRecapBooking?.id;
          setPostRecapBooking(null);
          if (bid) setMetricsWizardBookingId(bid);
        }}
        booking={postRecapBooking}
        onRecapSubmitted={() => {
          const bid = postRecapBooking?.booking_id || postRecapBooking?.id;
          setPostRecapBooking(null);
          fetchTodaysBookings();
          if (bid) setMetricsWizardBookingId(bid);
        }}
      />

      {/* Iter-235l — Shift Metrics Wizard auto-launch after recap.
          Talent can skip with the Close button; the wizard is optional
          per the user's Phase 1 spec (24h reminder if they skip). */}
      {metricsWizardBookingId && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-3 overflow-y-auto" data-testid="metrics-wizard-overlay">
          <div className="bg-white rounded-xl p-4 w-full max-w-2xl max-h-[95vh] overflow-y-auto">
            <Rsuspense fallback={
              <div className="flex items-center justify-center py-10 text-gray-400">Loading shift recap…</div>
            }>
              <ShiftMetricsWizard
                bookingId={metricsWizardBookingId}
                onClose={() => setMetricsWizardBookingId(null)}
                onSubmitted={() => {
                  setMetricsWizardBookingId(null);
                  fetchTodaysBookings();
                }}
              />
            </Rsuspense>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClockInOut;
